(function (root, factory) {
  'use strict';

  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.NRSelfServeBuilder = api;

  if (root && root.document) {
    if (root.document.readyState === 'loading') {
      root.document.addEventListener('DOMContentLoaded', api.initializeAll, { once: true });
    } else {
      api.initializeAll();
    }
  }
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  var STORAGE_KEY = 'nr-self-serve-builder-v1';
  var SESSION_PATTERN = /^[A-Za-z0-9_-]{8,128}$/;

  function createState() {
    return {
      stage: 'preferences',
      answers: emptyAnswers(),
      category: 'all',
      search: '',
      sessionId: null,
      presetHandle: null,
      selections: {},
      catalogHandles: []
    };
  }

  function validSessionId(value) {
    return typeof value === 'string' && SESSION_PATTERN.test(value);
  }

  function ensureSessionId(state) {
    if (validSessionId(state.sessionId)) return state.sessionId;
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      state.sessionId = crypto.randomUUID();
    } else {
      state.sessionId = 'nrsb-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
    }
    return state.sessionId;
  }

  function element(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }

  function parseJSON(rootNode, selector) {
    var script = rootNode.querySelector(selector);
    if (!script) return null;
    try { return JSON.parse(script.textContent); } catch (error) { return null; }
  }

  function emptyAnswers() {
    return {
      space: { widthFt: null, depthFt: null, heightFt: null },
      simulation: null,
      environment: null
    };
  }

  function normalizeNumber(value) {
    var number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : null;
  }

  function normalizeAnswers(answers) {
    answers = answers || {};
    var space = answers.space || {};
    return {
      space: {
        widthFt: normalizeNumber(space.widthFt),
        depthFt: normalizeNumber(space.depthFt),
        heightFt: normalizeNumber(space.heightFt)
      },
      simulation: answers.simulation === 'yes' || answers.simulation === 'no' ? answers.simulation : null,
      environment: answers.environment === 'indoor' || answers.environment === 'outdoor' ? answers.environment : null
    };
  }

  function restoreState(catalog) {
    var state = createState();
    if (typeof localStorage === 'undefined') return state;
    try {
      var saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (!saved || typeof saved !== 'object') return state;
      state.stage = saved.stage === 'catalog' ? 'catalog' : 'preferences';
      state.answers = normalizeAnswers(saved.answers);
      state.category = typeof saved.category === 'string' ? saved.category : 'all';
      state.sessionId = validSessionId(saved.sessionId) ? saved.sessionId : null;
      state.selections = validateSelections(saved.selections, catalog);
      state.presetHandle = typeof saved.presetHandle === 'string' && state.selections[saved.presetHandle]
        ? saved.presetHandle
        : null;
      state.catalogHandles = Array.isArray(saved.catalogHandles)
        ? saved.catalogHandles.filter(function (handle) { return typeof handle === 'string' && handle; })
        : [];
      return state;
    } catch (error) {
      return state;
    }
  }

  function validateSelections(selections, catalog) {
    var valid = {};
    if (!selections || typeof selections !== 'object') return valid;
    var products = catalog && Array.isArray(catalog.products) ? catalog.products : [];
    Object.keys(selections).forEach(function (handle) {
      var product = products.find(function (candidate) { return candidate.handle === handle; });
      var variantId = Number(selections[handle] && selections[handle].variantId);
      var variant = product && product.variants.find(function (candidate) {
        return candidate.id === variantId && candidate.available;
      });
      if (product && product.available && variant) valid[handle] = { handle: handle, variantId: variant.id };
    });
    return valid;
  }

  function persist(context) {
    ensureSessionId(context.state);
    context.state.catalogHandles = (context.catalog.products || []).map(function (product) { return product.handle; }).filter(Boolean);
    if (typeof localStorage === 'undefined') {
      context.storageReady = false;
      return false;
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        stage: context.state.stage,
        answers: context.state.answers,
        category: context.state.category,
        sessionId: context.state.sessionId,
        presetHandle: context.state.presetHandle,
        selections: context.state.selections,
        catalogHandles: context.state.catalogHandles
      }));
      context.storageReady = true;
      return true;
    } catch (error) {
      context.storageReady = false;
      return false;
    }
  }

  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'USD'
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return '$' + (Number(cents || 0) / 100).toFixed(2);
    }
  }

  function availableVariants(product) {
    return Array.isArray(product.variants) ? product.variants.filter(function (variant) { return variant.available; }) : [];
  }

  function productPrice(product, currency) {
    var prices = availableVariants(product).map(function (variant) { return Number(variant.price); });
    if (!prices.length) return 'Unavailable';
    var minimum = Math.min.apply(Math, prices);
    var prefix = prices.some(function (price) { return price !== minimum; }) ? 'From ' : '';
    return prefix + formatMoney(minimum, currency);
  }

  function productByHandle(catalog, handle) {
    return (catalog.products || []).find(function (product) { return product.handle === handle; }) || null;
  }

  function presetByHandle(context, handle) {
    return (context.presets || []).find(function (preset) { return preset.handle === handle; }) || null;
  }

  function seedPresetSelection(state, catalog, handle, replaceExisting) {
    var product = productByHandle(catalog, handle);
    var variants = product ? availableVariants(product) : [];
    if (!product || !product.available || !variants.length) return false;
    if (replaceExisting) state.selections = {};
    state.selections[handle] = { handle: handle, variantId: variants[0].id };
    state.presetHandle = handle;
    state.stage = 'catalog';
    state.category = 'all';
    state.search = '';
    return true;
  }

  function displayVariantTitle(productTitle, variantTitle) {
    var title = String(variantTitle || '').trim();
    if (!title || title === 'Default Title') return '';
    if (title.toLowerCase() === String(productTitle || '').trim().toLowerCase()) return '';
    return title;
  }

  function selectedVariant(context, product) {
    var selection = context.state.selections[product.handle];
    if (!selection) return null;
    return product.variants.find(function (variant) { return variant.id === selection.variantId; }) || null;
  }

  function selectedItems(context) {
    return Object.keys(context.state.selections).map(function (handle) {
      var product = productByHandle(context.catalog, handle);
      var variant = product ? selectedVariant(context, product) : null;
      return product && variant ? {
        product: product,
        variant: variant,
        isPreset: context.state.presetHandle === handle,
        preset: presetByHandle(context, handle)
      } : null;
    }).filter(Boolean);
  }

  function subtotal(context) {
    return selectedItems(context).reduce(function (sum, item) { return sum + Number(item.variant.price || 0); }, 0);
  }

  function updateUrl(builderMode, replace, catalogMode, presetHandle) {
    if (typeof history === 'undefined') return;
    try {
      var url = new URL(window.location.href);
      if (builderMode) url.searchParams.set('builder', '1');
      else url.searchParams.delete('builder');
      if (builderMode && catalogMode) url.searchParams.set('catalog', '1');
      else url.searchParams.delete('catalog');
      if (builderMode && presetHandle) url.searchParams.set('start', presetHandle);
      else url.searchParams.delete('start');
      history[replace ? 'replaceState' : 'pushState']({ nrSelfServeBuilder: builderMode }, '', url.pathname + url.search + url.hash);
    } catch (error) {
      return;
    }
  }

  function builderRequested() {
    try { return new URL(window.location.href).searchParams.get('builder') === '1'; } catch (error) { return false; }
  }

  function catalogRequested() {
    try { return new URL(window.location.href).searchParams.get('catalog') === '1'; } catch (error) { return false; }
  }

  function presetRequested() {
    try { return new URL(window.location.href).searchParams.get('start'); } catch (error) { return null; }
  }

  function urlPresetHandle(context, requestedHandle) {
    var handle = requestedHandle || presetRequested();
    return handle && context.state.presetHandle === handle && context.state.selections[handle]
      ? handle
      : null;
  }

  function buildTopbar(context) {
    var bar = element('div', 'nrsb__topbar');
    var back = element('button', 'nrsb__back', '← Back to guide');
    back.type = 'button';
    back.dataset.nrsbAction = 'guide';
    bar.appendChild(back);
    bar.appendChild(element('span', 'nrsb__brand', 'The Net Return · Setup Builder'));
    var reset = element('button', 'nrsb__quiet-action', 'Start over');
    reset.type = 'button';
    reset.dataset.nrsbAction = 'reset';
    bar.appendChild(reset);
    return bar;
  }

  function renderShell(context) {
    var app = element('div', 'nrsb');
    app.appendChild(buildTopbar(context));
    var main = element('div', 'nrsb__main');
    main.dataset.nrsbMain = '';
    app.appendChild(main);
    context.shell.replaceChildren(app);
    if (context.state.stage === 'catalog') renderCatalog(context);
    else renderPreferences(context);
  }

  function optionalHeading(text) {
    var head = element('div', 'nrsb-question__head');
    head.appendChild(element('h2', '', text));
    head.appendChild(element('span', 'nrsb-question__optional', 'Optional'));
    return head;
  }

  function spaceField(context, label, key) {
    var field = element('label', 'nrsb-field');
    field.appendChild(element('span', '', label));
    var input = element('input');
    input.type = 'number';
    input.min = '1';
    input.step = '0.5';
    input.inputMode = 'decimal';
    input.placeholder = 'Feet';
    input.value = context.state.answers.space[key] || '';
    input.dataset.nrsbSpace = key;
    field.appendChild(input);
    return field;
  }

  function choice(context, name, value, label, checked) {
    var wrapper = element('label', 'nrsb-choice');
    var input = element('input');
    input.type = 'radio';
    input.name = name;
    input.value = value;
    input.checked = checked;
    input.dataset.nrsbPreference = name;
    wrapper.appendChild(input);
    wrapper.appendChild(element('span', '', label));
    return wrapper;
  }

  function clearButton(name, visible) {
    var button = element('button', 'nrsb__quiet-action', 'Clear');
    button.type = 'button';
    button.dataset.nrsbClear = name;
    button.hidden = !visible;
    return button;
  }

  function renderPreferences(context) {
    context.state.stage = 'preferences';
    persist(context);
    var main = context.shell.querySelector('[data-nrsb-main]');
    main.replaceChildren();

    var intro = element('header', 'nrsb__intro');
    intro.appendChild(element('h1', '', 'Build your setup'));
    intro.appendChild(element('p', '', 'Answer as much or as little as you want. Nothing is removed from the catalog.'));
    main.appendChild(intro);

    var form = element('form', 'nrsb-preferences');
    form.dataset.nrsbPreferences = '';

    var space = element('section', 'nrsb-question');
    space.appendChild(optionalHeading('Your space requirements'));
    var dimensions = element('div', 'nrsb-space-fields');
    dimensions.appendChild(spaceField(context, 'Width', 'widthFt'));
    dimensions.appendChild(spaceField(context, 'Depth', 'depthFt'));
    dimensions.appendChild(spaceField(context, 'Height', 'heightFt'));
    space.appendChild(dimensions);
    form.appendChild(space);

    var simulation = element('section', 'nrsb-question');
    var simulationHead = optionalHeading('Golf simulation');
    simulationHead.appendChild(clearButton('simulation', Boolean(context.state.answers.simulation)));
    simulation.appendChild(simulationHead);
    var simulationChoices = element('div', 'nrsb-choice-row');
    simulationChoices.appendChild(choice(context, 'simulation', 'yes', 'Yes', context.state.answers.simulation === 'yes'));
    simulationChoices.appendChild(choice(context, 'simulation', 'no', 'No', context.state.answers.simulation === 'no'));
    simulation.appendChild(simulationChoices);
    form.appendChild(simulation);

    var environment = element('section', 'nrsb-question');
    var environmentHead = optionalHeading('Indoor or outdoor');
    environmentHead.appendChild(clearButton('environment', Boolean(context.state.answers.environment)));
    environment.appendChild(environmentHead);
    var environmentChoices = element('div', 'nrsb-choice-row');
    environmentChoices.appendChild(choice(context, 'environment', 'indoor', 'Indoor', context.state.answers.environment === 'indoor'));
    environmentChoices.appendChild(choice(context, 'environment', 'outdoor', 'Outdoor', context.state.answers.environment === 'outdoor'));
    environment.appendChild(environmentChoices);
    form.appendChild(environment);

    var actions = element('div', 'nrsb-preferences__actions');
    var browse = element('button', 'nrsb-button nrsb-button--primary', 'Browse catalog');
    browse.type = 'submit';
    actions.appendChild(browse);
    var skip = element('button', 'nrsb__quiet-action', 'Skip — I know what I want');
    skip.type = 'button';
    skip.dataset.nrsbAction = 'skip';
    actions.appendChild(skip);
    form.appendChild(actions);
    main.appendChild(form);
    focusHeading(main);
  }

  function fallbackRanking(catalog, answers) {
    var products = (catalog.products || []).filter(function (product) { return product.builderEnabled !== false; }).map(function (product, index) {
      return {
        product: product,
        handle: product.handle,
        title: product.title,
        sectionId: product.role || 'other',
        score: 0,
        reasonTags: [],
        primaryReason: null,
        selectable: Boolean(product.available && availableVariants(product).length),
        browseable: true,
        sourceIndex: index
      };
    });
    return { answers: answers, hasPreferences: false, products: products, sections: [] };
  }

  function rank(context) {
    if (typeof window !== 'undefined' && window.NRSelfServeRanking && typeof window.NRSelfServeRanking.rankCatalog === 'function') {
      return window.NRSelfServeRanking.rankCatalog(context.catalog, context.state.answers);
    }
    return fallbackRanking(context.catalog, context.state.answers);
  }

  function activePreferenceLabels(answers) {
    var labels = [];
    var dimensions = [];
    if (answers.space.widthFt) dimensions.push(answers.space.widthFt + '′ W');
    if (answers.space.depthFt) dimensions.push(answers.space.depthFt + '′ D');
    if (answers.space.heightFt) dimensions.push(answers.space.heightFt + '′ H');
    if (dimensions.length) labels.push(dimensions.join(' · '));
    if (answers.simulation === 'yes') labels.push('Simulation');
    if (answers.simulation === 'no') labels.push('Practice only');
    if (answers.environment === 'indoor') labels.push('Indoor');
    if (answers.environment === 'outdoor') labels.push('Outdoor');
    return labels;
  }

  function renderCatalog(context) {
    context.state.stage = 'catalog';
    context.state.answers = normalizeAnswers(context.state.answers);
    context.ranking = rank(context);
    persist(context);
    var main = context.shell.querySelector('[data-nrsb-main]');
    main.replaceChildren();

    var head = element('header', 'nrsb__catalog-head');
    var copy = element('div');
    var activePreset = context.state.presetHandle ? presetByHandle(context, context.state.presetHandle) : null;
    copy.appendChild(element('h1', '', activePreset ? 'Build from ' + activePreset.title : 'Browse catalog'));
    copy.appendChild(element('p', '', activePreset
      ? 'Your starting package is selected. Add, remove, or compare any other product below.'
      : context.ranking.hasPreferences
        ? 'Your answers change the order, not what you can browse.'
        : 'Everything stays available. Start with any category.'));
    var preferenceLabels = activePreferenceLabels(context.state.answers);
    if (preferenceLabels.length) {
      var reasons = element('div', 'nrsb-reasons nrsb-reasons--inline');
      preferenceLabels.forEach(function (label) { reasons.appendChild(element('span', 'nrsb-reason', label)); });
      copy.appendChild(reasons);
    }
    head.appendChild(copy);
    var edit = element('button', 'nrsb-button nrsb-button--secondary nrsb__edit-preferences', 'Edit preferences');
    edit.type = 'button';
    edit.dataset.nrsbAction = 'preferences';
    head.appendChild(edit);
    main.appendChild(head);

    var tools = element('div', 'nrsb-catalog-tools');
    var tabs = element('div', 'nrsb-category-tabs');
    tabs.setAttribute('aria-label', 'Catalog categories');
    tabs.appendChild(categoryButton('all', 'All products', context.state.category === 'all'));
    context.ranking.sections.forEach(function (section) {
      tabs.appendChild(categoryButton(section.id, section.label, context.state.category === section.id));
    });
    tools.appendChild(tabs);
    var search = element('div', 'nrsb-search');
    var searchLabel = element('label');
    searchLabel.appendChild(element('span', '', 'Search products'));
    var searchInput = element('input');
    searchInput.type = 'search';
    searchInput.placeholder = 'Search products';
    searchInput.value = context.state.search;
    searchInput.dataset.nrsbSearch = '';
    searchLabel.appendChild(searchInput);
    search.appendChild(searchLabel);
    tools.appendChild(search);
    main.appendChild(tools);

    var layout = element('div', 'nrsb-catalog-layout');
    var results = element('section', 'nrsb-results');
    results.dataset.nrsbResults = '';
    layout.appendChild(results);
    layout.appendChild(renderSetup(context));
    main.appendChild(layout);
    renderResults(context);
    focusHeading(main);
  }

  function categoryButton(id, label, pressed) {
    var button = element('button', 'nrsb-category-tab', label);
    button.type = 'button';
    button.dataset.nrsbCategory = id;
    button.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    return button;
  }

  function currentProducts(context) {
    var query = String(context.state.search || '').trim().toLowerCase();
    return context.ranking.products.filter(function (entry) {
      if (!entry.browseable || !entry.product || entry.product.builderEnabled === false) return false;
      if (context.state.category !== 'all' && entry.sectionId !== context.state.category) return false;
      if (!query) return true;
      var text = [entry.product.title, entry.product.vendor, entry.sectionId]
        .concat(entry.reasonTags.map(function (reason) { return reason.label; }))
        .join(' ').toLowerCase();
      return text.indexOf(query) !== -1;
    });
  }

  function renderResults(context) {
    var container = context.shell.querySelector('[data-nrsb-results]');
    if (!container) return;
    container.replaceChildren();
    var products = currentProducts(context);
    var heading = element('div', 'nrsb-results-head');
    heading.appendChild(element('h2', '', context.state.category === 'all' ? 'Catalog' : categoryLabel(context, context.state.category)));
    heading.appendChild(element('span', '', products.length + (products.length === 1 ? ' product' : ' products')));
    container.appendChild(heading);
    if (!products.length) {
      container.appendChild(element('p', 'nrsb-empty', 'No products match this view. Try another category or search.'));
      return;
    }
    var grid = element('div', 'nrsb-product-grid');
    products.forEach(function (entry) { grid.appendChild(renderProductCard(context, entry)); });
    container.appendChild(grid);
  }

  function categoryLabel(context, id) {
    var section = context.ranking.sections.find(function (candidate) { return candidate.id === id; });
    return section ? section.label : 'Catalog';
  }

  function renderProductCard(context, entry) {
    var product = entry.product;
    var variants = availableVariants(product);
    var selected = context.state.selections[product.handle];
    var card = element('article', 'nrsb-product-card' + (selected ? ' is-selected' : '') + (!entry.selectable ? ' is-unavailable' : ''));
    card.dataset.nrsbCard = product.handle;

    var media = element('div', 'nrsb-product-card__media');
    if (product.image && product.image.url) {
      var image = element('img');
      image.src = product.image.url;
      image.alt = product.image.alt || product.title;
      image.loading = 'lazy';
      image.width = 640;
      image.height = 480;
      media.appendChild(image);
    }
    if (entry.reasonTags.length) {
      var reasons = element('div', 'nrsb-reasons');
      entry.reasonTags.slice(0, 2).forEach(function (reason) { reasons.appendChild(element('span', 'nrsb-reason', reason.label)); });
      media.appendChild(reasons);
    }
    card.appendChild(media);

    var body = element('div', 'nrsb-product-card__body');
    var meta = element('div', 'nrsb-product-card__meta');
    meta.appendChild(element('span', '', categoryLabel(context, entry.sectionId)));
    meta.appendChild(element('span', '', productPrice(product, context.catalog.currency)));
    body.appendChild(meta);
    body.appendChild(element('h3', '', product.title));

    var select = null;
    if (variants.length > 1) {
      select = element('select', 'nrsb-variant');
      select.setAttribute('aria-label', 'Choose ' + product.title + ' option');
      select.dataset.nrsbVariant = product.handle;
      variants.forEach(function (variant) {
        var option = element('option', '', variant.title + ' — ' + formatMoney(variant.price, context.catalog.currency));
        option.value = String(variant.id);
        if (selected && selected.variantId === variant.id) option.selected = true;
        select.appendChild(option);
      });
      body.appendChild(select);
    }

    var actions = element('div', 'nrsb-product-card__actions');
    var add = element('button', 'nrsb-button nrsb-button--primary', selected ? 'Remove' : entry.selectable ? 'Add to setup' : 'Unavailable');
    add.type = 'button';
    add.dataset.nrsbAdd = product.handle;
    add.disabled = !selected && (!entry.selectable || !variants.length);
    actions.appendChild(add);
    var details = element('a', 'nrsb-button nrsb-button--secondary', 'View');
    details.dataset.nrsbDetails = product.handle;
    details.href = productDetailsUrl(context, product, selected ? selected.variantId : variants[0] && variants[0].id);
    actions.appendChild(details);
    body.appendChild(actions);
    card.appendChild(body);
    return card;
  }

  function productUrlWithVariant(url, variantId) {
    try {
      var parsed = new URL(url, window.location.origin);
      if (variantId) parsed.searchParams.set('variant', String(variantId));
      return parsed.pathname + parsed.search;
    } catch (error) {
      return url;
    }
  }

  function productUrlWithSession(url, variantId, sessionId) {
    if (!validSessionId(sessionId)) return productUrlWithVariant(url, variantId);
    try {
      var parsed = new URL(url, window.location.origin);
      if (variantId) parsed.searchParams.set('variant', String(variantId));
      parsed.searchParams.set('nr_builder_session', sessionId);
      parsed.searchParams.set('nr_builder_source', 'self-serve');
      return parsed.pathname + parsed.search;
    } catch (error) {
      return productUrlWithVariant(url, variantId);
    }
  }

  function productDetailsUrl(context, product, variantId) {
    if (!context.storageReady || !validSessionId(context.state.sessionId)) {
      return productUrlWithVariant(product.url, variantId);
    }
    return productUrlWithSession(product.url, variantId, context.state.sessionId);
  }

  function readSessionState(sessionId) {
    if (typeof localStorage === 'undefined') return { valid: false, code: 'storage_unavailable' };
    try {
      var state = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (!state || typeof state !== 'object' || state.sessionId !== sessionId || !validSessionId(state.sessionId)) {
        return { valid: false, code: 'invalid_session' };
      }
      if (!state.selections || typeof state.selections !== 'object' || Array.isArray(state.selections)) {
        return { valid: false, code: 'invalid_session' };
      }
      state.catalogHandles = Array.isArray(state.catalogHandles)
        ? state.catalogHandles.filter(function (handle) { return typeof handle === 'string' && handle; })
        : [];
      return { valid: true, state: state };
    } catch (error) {
      return { valid: false, code: 'storage_unavailable' };
    }
  }

  function returnToBuilderUrl(canonicalPath, sessionId) {
    try {
      var url = new URL(canonicalPath || '/pages/build-your-setup', window.location.origin);
      if (url.origin !== window.location.origin) throw new Error('invalid_builder_origin');
      url.searchParams.set('builder', '1');
      url.searchParams.set('catalog', '1');
      url.searchParams.set('nr_builder_session', sessionId);
      url.searchParams.set('nr_builder_source', 'self-serve');
      url.searchParams.delete('start');
      return url.pathname + url.search + url.hash;
    } catch (error) {
      return '/pages/build-your-setup?builder=1&catalog=1';
    }
  }

  function renderPdpRecovery(template, code) {
    var fragment = template.content.cloneNode(true);
    var root = fragment.querySelector('[data-nr-builder-return]');
    if (!root) return;
    var content = root.querySelector('[data-nr-builder-content]');
    var action = root.querySelector('[data-nr-builder-action]');
    var status = root.querySelector('[data-nr-builder-status]');
    var recovery = root.querySelector('[data-nr-builder-recovery]');
    var message = root.querySelector('[data-nr-builder-recovery-message]');
    var link = root.querySelector('[data-nr-builder-start-new]');
    if (content) content.hidden = true;
    if (action) action.hidden = true;
    if (status) status.hidden = true;
    if (recovery) recovery.hidden = false;
    if (message) message.textContent = code === 'storage_unavailable' ? root.dataset.stringStorageError : root.dataset.stringExpired;
    if (link) link.href = template.dataset.canonicalBuilderPath || '/pages/build-your-setup';
    root.hidden = false;
    template.after(fragment);
  }

  function pdpVariant(product, variantId) {
    return (product && Array.isArray(product.variants) ? product.variants : []).find(function (variant) {
      return Number(variant.id) === Number(variantId);
    }) || null;
  }

  function initializePdpReturn(template) {
    if (!template || template.dataset.nrBuilderInitialized === 'true') return null;
    var params = new URL(window.location.href).searchParams;
    var sessionId = params.get('nr_builder_session');
    if (params.get('nr_builder_source') !== 'self-serve' || !validSessionId(sessionId)) return null;
    template.dataset.nrBuilderInitialized = 'true';

    var loaded = readSessionState(sessionId);
    if (!loaded.valid) {
      renderPdpRecovery(template, loaded.code);
      return loaded;
    }

    var productNode = template.content.querySelector('[data-nr-builder-product]');
    var product;
    try {
      product = JSON.parse(productNode.textContent);
    } catch (error) {
      renderPdpRecovery(template, 'invalid_session');
      return { valid: false, code: 'invalid_product_data' };
    }
    if (!product || loaded.state.catalogHandles.indexOf(product.handle) === -1) {
      renderPdpRecovery(template, 'invalid_session');
      return { valid: false, code: 'product_outside_session_catalog' };
    }

    var fragment = template.content.cloneNode(true);
    var banner = fragment.querySelector('[data-nr-builder-return]');
    var productJson = fragment.querySelector('[data-nr-builder-product]');
    if (productJson) productJson.remove();
    var backLink = banner.querySelector('[data-nr-builder-back]');
    var heading = banner.querySelector('[data-nr-builder-heading]');
    var countNode = banner.querySelector('[data-nr-builder-count]');
    var action = banner.querySelector('[data-nr-builder-action]');
    var status = banner.querySelector('[data-nr-builder-status]');
    var form = document.getElementById(template.dataset.productFormId || '');
    var selectedVariantId = Number(params.get('variant')) || Number(template.dataset.selectedVariantId);

    backLink.href = returnToBuilderUrl(template.dataset.canonicalBuilderPath, sessionId);
    heading.textContent = banner.dataset.stringBack.replace('@base@', 'saved');

    function refresh() {
      var variant = pdpVariant(product, selectedVariantId);
      var existing = loaded.state.selections[product.handle];
      var count = Object.keys(loaded.state.selections).length;
      countNode.textContent = String(count === 1 ? banner.dataset.stringItemOne : banner.dataset.stringItemOther)
        .replace('@count@', String(count));
      action.textContent = existing ? banner.dataset.stringUpdate : banner.dataset.stringAdd;
      action.disabled = !product.available || !variant || variant.available === false;
      status.textContent = action.disabled ? banner.dataset.stringUnavailable : banner.dataset.stringSubtotalPending;
      banner.dataset.nrBuilderEligibility = action.disabled ? 'variant_unavailable' : 'eligible';
    }

    if (form) {
      form.addEventListener('variant:changed', function (event) {
        selectedVariantId = event.detail && event.detail.variant ? Number(event.detail.variant.id) : 0;
        refresh();
      });
    }

    action.addEventListener('click', function () {
      var variant = pdpVariant(product, selectedVariantId);
      if (!product.available || !variant || variant.available === false) {
        refresh();
        return;
      }
      var updating = Boolean(loaded.state.selections[product.handle]);
      loaded.state.selections[product.handle] = { handle: product.handle, variantId: Number(variant.id) };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(loaded.state));
        refresh();
        status.textContent = updating ? banner.dataset.stringUpdated : banner.dataset.stringAdded;
      } catch (error) {
        status.textContent = banner.dataset.stringStorageError;
      }
    });

    banner.hidden = false;
    template.after(fragment);
    refresh();
    return { valid: true, state: loaded.state, product: product };
  }

  function renderSetup(context) {
    var details = element('details', 'nrsb-setup');
    details.open = true;
    details.dataset.nrsbSetup = '';
    var summary = element('summary');
    summary.appendChild(element('strong', '', 'Your setup'));
    summary.appendChild(element('span', 'nrsb-setup__count', String(selectedItems(context).length)));
    details.appendChild(summary);
    var body = element('div', 'nrsb-setup__body');
    body.dataset.nrsbSetupBody = '';
    details.appendChild(body);
    fillSetup(context, body);
    return details;
  }

  function appendSetupImage(container, item) {
    var media = element('div', 'nrsb-setup__media');
    if (item.product.image && item.product.image.url) {
      var image = element('img');
      image.src = item.product.image.url;
      image.alt = item.product.image.alt || item.product.title;
      image.loading = 'lazy';
      image.width = 128;
      image.height = 128;
      media.appendChild(image);
    }
    container.appendChild(media);
  }

  function appendPresetIncludes(container, preset) {
    if (!preset || !Array.isArray(preset.includes) || !preset.includes.length) return;
    var list = element('ul', 'nrsb-setup__includes');
    preset.includes.filter(Boolean).forEach(function (includedItem) {
      list.appendChild(element('li', '', includedItem));
    });
    if (list.children.length) container.appendChild(list);
  }

  function fillSetup(context, body) {
    body.replaceChildren();
    var items = selectedItems(context);
    if (!items.length) {
      body.appendChild(element('p', 'nrsb-setup__empty', 'Add any product. You can start anywhere and change your setup at any time.'));
      return;
    }
    items.forEach(function (item) {
      var row = element('div', 'nrsb-setup__item' + (item.isPreset ? ' is-preset' : ''));
      appendSetupImage(row, item);
      var text = element('div', 'nrsb-setup__item-copy');
      text.appendChild(element('span', 'nrsb-setup__item-label', item.isPreset ? 'Starting bundle' : 'Added product'));
      text.appendChild(element('strong', '', item.product.title));
      var variantTitle = displayVariantTitle(item.product.title, item.variant.title);
      if (variantTitle) text.appendChild(element('span', 'nrsb-setup__variant', variantTitle));
      if (item.isPreset && item.preset && item.preset.summary) {
        text.appendChild(element('p', 'nrsb-setup__summary', item.preset.summary));
      }
      if (item.isPreset) appendPresetIncludes(text, item.preset);
      row.appendChild(text);
      var controls = element('div', 'nrsb-setup__item-controls');
      controls.appendChild(element('strong', 'nrsb-setup__price', formatMoney(item.variant.price, context.catalog.currency)));
      var remove = element('button', 'nrsb-setup__remove', 'Remove');
      remove.type = 'button';
      remove.dataset.nrsbRemove = item.product.handle;
      controls.appendChild(remove);
      row.appendChild(controls);
      body.appendChild(row);
    });
    var total = element('div', 'nrsb-setup__total');
    total.appendChild(element('span', '', 'Subtotal'));
    total.appendChild(element('strong', '', formatMoney(subtotal(context), context.catalog.currency)));
    body.appendChild(total);
    var cart = element('button', 'nrsb-button nrsb-button--primary nrsb-setup__checkout', 'Add setup to cart');
    cart.type = 'button';
    cart.dataset.nrsbAction = 'cart';
    body.appendChild(cart);
    var status = element('p', 'nrsb-status');
    status.dataset.nrsbStatus = '';
    status.setAttribute('role', 'status');
    status.hidden = true;
    body.appendChild(status);
  }

  function refreshSelectionUI(context) {
    context.shell.querySelectorAll('[data-nrsb-card]').forEach(function (card) {
      var handle = card.dataset.nrsbCard;
      var selected = Boolean(context.state.selections[handle]);
      card.classList.toggle('is-selected', selected);
      var button = card.querySelector('[data-nrsb-add]');
      if (button && !button.disabled) button.textContent = selected ? 'Remove' : 'Add to setup';
      var details = card.querySelector('[data-nrsb-details]');
      var product = productByHandle(context.catalog, handle);
      if (details && product) details.href = productDetailsUrl(context, product, selectedVariantIdForCard(context, handle));
    });
    var setup = context.shell.querySelector('[data-nrsb-setup]');
    if (!setup) return;
    setup.querySelector('.nrsb-setup__count').textContent = String(selectedItems(context).length);
    fillSetup(context, setup.querySelector('[data-nrsb-setup-body]'));
  }

  function selectedVariantIdForCard(context, handle) {
    var card = context.shell.querySelector('[data-nrsb-card="' + escapeAttribute(handle) + '"]');
    var select = card && card.querySelector('[data-nrsb-variant]');
    if (select) return Number(select.value);
    var product = productByHandle(context.catalog, handle);
    var variants = product ? availableVariants(product) : [];
    return variants.length ? variants[0].id : null;
  }

  function escapeAttribute(value) {
    return String(value || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  function readPreferenceInputs(context) {
    var form = context.shell.querySelector('[data-nrsb-preferences]');
    if (!form) return;
    context.state.answers.space.widthFt = normalizeNumber(form.querySelector('[data-nrsb-space="widthFt"]').value);
    context.state.answers.space.depthFt = normalizeNumber(form.querySelector('[data-nrsb-space="depthFt"]').value);
    context.state.answers.space.heightFt = normalizeNumber(form.querySelector('[data-nrsb-space="heightFt"]').value);
    var simulation = form.querySelector('input[name="simulation"]:checked');
    var environment = form.querySelector('input[name="environment"]:checked');
    context.state.answers.simulation = simulation ? simulation.value : null;
    context.state.answers.environment = environment ? environment.value : null;
  }

  function enterCatalog(context, skip) {
    if (skip) context.state.answers = emptyAnswers();
    else readPreferenceInputs(context);
    context.state.category = 'all';
    context.state.search = '';
    renderCatalog(context);
    updateUrl(true, true, true, urlPresetHandle(context));
    scrollToRoot(context);
  }

  function reset(context) {
    context.state = createState();
    persist(context);
    renderPreferences(context);
    updateUrl(true, true, false, null);
    scrollToRoot(context);
  }

  function scrollToRoot(context) {
    if (context.root && typeof context.root.scrollIntoView === 'function') context.root.scrollIntoView({ block: 'start', behavior: 'auto' });
  }

  function focusHeading(container) {
    var heading = container.querySelector('h1');
    if (!heading || typeof heading.focus !== 'function') return;
    heading.tabIndex = -1;
    heading.focus({ preventScroll: true });
  }

  function showGuide(context, pushHistory) {
    context.root.querySelector('[data-nrc-guide]').hidden = false;
    context.shell.hidden = true;
    context.root.dataset.nrcMode = 'guide';
    updateUrl(false, !pushHistory);
    scrollToRoot(context);
  }

  function enterBuilder(context, pushHistory, requestedPresetHandle) {
    context.root.querySelector('[data-nrc-guide]').hidden = true;
    context.shell.hidden = false;
    context.root.dataset.nrcMode = 'builder';
    renderShell(context);
    updateUrl(true, !pushHistory, context.state.stage === 'catalog', urlPresetHandle(context, requestedPresetHandle));
    scrollToRoot(context);
  }

  function addSetupToCart(context, button) {
    var items = selectedItems(context).map(function (item) { return { id: item.variant.id, quantity: 1 }; });
    if (!items.length || typeof fetch !== 'function') return;
    var status = context.shell.querySelector('[data-nrsb-status]');
    button.disabled = true;
    button.textContent = 'Adding…';
    if (status) status.hidden = true;
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (response) {
      if (!response.ok) throw new Error('cart_add_failed');
      return response.json();
    }).then(function () {
      window.location.assign('/cart');
    }).catch(function () {
      button.disabled = false;
      button.textContent = 'Add setup to cart';
      if (status) {
        status.textContent = 'We could not add the setup. Your selections are still saved.';
        status.hidden = false;
      }
    });
  }

  function onClick(context, event) {
    var details = event.target.closest('[data-nrsb-details]');
    if (details) {
      var detailsHandle = details.dataset.nrsbDetails;
      var detailsProduct = productByHandle(context.catalog, detailsHandle);
      if (detailsProduct) {
        var detailsVariantId = selectedVariantIdForCard(context, detailsHandle);
        if (persist(context)) details.href = productDetailsUrl(context, detailsProduct, detailsVariantId);
        else details.href = productUrlWithVariant(detailsProduct.url, detailsVariantId);
      }
      return;
    }

    var guideAction = event.target.closest('[data-nrc-action]');
    if (guideAction && guideAction.dataset.nrcAction === 'start-bundle') {
      var presetHandle = guideAction.dataset.nrcStartHandle;
      event.preventDefault();
      if (!seedPresetSelection(context.state, context.catalog, presetHandle, true)) {
        context.state.presetHandle = null;
        context.state.stage = 'catalog';
      }
      persist(context);
      return enterBuilder(context, true, presetHandle);
    }
    if (guideAction && guideAction.dataset.nrcAction === 'enter-builder') {
      event.preventDefault();
      context.state.presetHandle = null;
      context.state.stage = 'preferences';
      return enterBuilder(context, true);
    }
    if (guideAction && guideAction.dataset.nrcAction === 'browse-catalog') {
      event.preventDefault();
      context.state.answers = emptyAnswers();
      context.state.category = 'all';
      context.state.search = '';
      context.state.stage = 'catalog';
      return enterBuilder(context, true);
    }

    var action = event.target.closest('[data-nrsb-action]');
    if (action) {
      event.preventDefault();
      if (action.dataset.nrsbAction === 'guide') return showGuide(context, true);
      if (action.dataset.nrsbAction === 'reset') return reset(context);
      if (action.dataset.nrsbAction === 'preferences') return renderPreferences(context);
      if (action.dataset.nrsbAction === 'skip') return enterCatalog(context, true);
      if (action.dataset.nrsbAction === 'cart') return addSetupToCart(context, action);
    }

    var clear = event.target.closest('[data-nrsb-clear]');
    if (clear) {
      event.preventDefault();
      context.state.answers[clear.dataset.nrsbClear] = null;
      renderPreferences(context);
      return;
    }

    var category = event.target.closest('[data-nrsb-category]');
    if (category) {
      context.state.category = category.dataset.nrsbCategory;
      persist(context);
      context.shell.querySelectorAll('[data-nrsb-category]').forEach(function (button) {
        button.setAttribute('aria-pressed', button === category ? 'true' : 'false');
      });
      renderResults(context);
      return;
    }

    var add = event.target.closest('[data-nrsb-add]');
    if (add) {
      var handle = add.dataset.nrsbAdd;
      if (context.state.selections[handle]) {
        delete context.state.selections[handle];
        if (context.state.presetHandle === handle) context.state.presetHandle = null;
      }
      else {
        var variantId = selectedVariantIdForCard(context, handle);
        if (variantId) context.state.selections[handle] = { handle: handle, variantId: variantId };
      }
      persist(context);
      updateUrl(true, true, true, null);
      refreshSelectionUI(context);
      return;
    }

    var remove = event.target.closest('[data-nrsb-remove]');
    if (remove) {
      var removedHandle = remove.dataset.nrsbRemove;
      delete context.state.selections[removedHandle];
      if (context.state.presetHandle === removedHandle) context.state.presetHandle = null;
      persist(context);
      updateUrl(true, true, true, null);
      refreshSelectionUI(context);
    }
  }

  function onSubmit(context, event) {
    if (!event.target.matches('[data-nrsb-preferences]')) return;
    event.preventDefault();
    enterCatalog(context, false);
  }

  function onInput(context, event) {
    if (event.target.matches('[data-nrsb-search]')) {
      context.state.search = event.target.value;
      renderResults(context);
    }
  }

  function onChange(context, event) {
    if (event.target.matches('[data-nrsb-variant]')) {
      var handle = event.target.dataset.nrsbVariant;
      if (context.state.selections[handle]) {
        context.state.selections[handle].variantId = Number(event.target.value);
        persist(context);
        updateUrl(true, true, true, null);
        refreshSelectionUI(context);
      }
    }
  }

  function applyRequestedBuilderState(context) {
    var requestedPreset = presetRequested();
    if (requestedPreset) {
      var restoredPreset = context.state.presetHandle === requestedPreset && Boolean(context.state.selections[requestedPreset]);
      var presetReady = restoredPreset || seedPresetSelection(context.state, context.catalog, requestedPreset, true);
      if (!presetReady) context.state.stage = catalogRequested() ? 'catalog' : 'preferences';
      else if (catalogRequested()) context.state.stage = 'catalog';
      return;
    }
    context.state.stage = catalogRequested() ? 'catalog' : 'preferences';
  }

  function initialize(rootNode) {
    var catalog = parseJSON(rootNode, '[data-nrc-catalog]');
    var presetRegistry = parseJSON(rootNode, '[data-nrc-bundle-presets]');
    var shell = rootNode.querySelector('[data-nrc-builder-shell]');
    if (!catalog || !Array.isArray(catalog.products) || !shell) return null;
    var context = {
      root: rootNode,
      shell: shell,
      catalog: catalog,
      presets: presetRegistry && Array.isArray(presetRegistry.presets) ? presetRegistry.presets : [],
      state: restoreState(catalog),
      ranking: null
    };

    rootNode.addEventListener('click', function (event) { onClick(context, event); });
    rootNode.addEventListener('submit', function (event) { onSubmit(context, event); });
    rootNode.addEventListener('input', function (event) { onInput(context, event); });
    rootNode.addEventListener('change', function (event) { onChange(context, event); });
    window.addEventListener('popstate', function () {
      if (builderRequested()) {
        applyRequestedBuilderState(context);
        enterBuilder(context, false);
      }
      else showGuide(context, false);
    });

    if (builderRequested()) {
      applyRequestedBuilderState(context);
      enterBuilder(context, false);
    }
    else showGuide(context, false);
    rootNode.nrSelfServeBuilder = context;
    return context;
  }

  function initializeAll() {
    return Array.prototype.map.call(document.querySelectorAll('[data-nrc-foundation]'), initialize).filter(Boolean);
  }

  return {
    initialize: initialize,
    initializeAll: initializeAll,
    normalizeAnswers: normalizeAnswers,
    validateSelections: validateSelections,
    seedPresetSelection: seedPresetSelection,
    displayVariantTitle: displayVariantTitle,
    productUrlWithVariant: productUrlWithVariant,
    productUrlWithSession: productUrlWithSession,
    returnToBuilderUrl: returnToBuilderUrl,
    urlPresetHandle: urlPresetHandle,
    applyRequestedBuilderState: applyRequestedBuilderState,
    initializePdpReturn: initializePdpReturn
  };
});
